import { Button } from './Button';
import { cx } from '../lib/cx';

/**
 * The control that finishes a keyset-paginated list.
 *
 * It exists because the failure mode it prevents is invisible: a list whose
 * server hands back a `nextCursor` nobody asks for does not look broken — it
 * looks like a list that happens to end there. Posts stopped at 50 rows and the
 * Review inbox at 100, with no 51st or 101st reachable by any means, and
 * nothing on screen said so.
 *
 * Shared rather than re-hand-rolled per screen so "there is more behind this"
 * reads identically everywhere, and so a list that forgets to render it is a
 * missing component rather than a missing div.
 */
export interface LoadMoreProps {
  /** `hasNextPage` from the infinite query — false once the cursor runs out. */
  readonly hasMore: boolean;
  /** `isFetchingNextPage`. Distinct from a background refetch of pages already held. */
  readonly isLoading: boolean;
  readonly onLoadMore: () => void;
  /** Overridden where "more" runs backwards, e.g. earlier messages in a thread. */
  readonly label?: string;
  readonly className?: string;
}

export function LoadMore({ hasMore, isLoading, onLoadMore, label = 'Load more', className }: LoadMoreProps) {
  // Nothing behind the last row: render nothing at all rather than a disabled
  // button, which would read as "there is more, but not for you".
  if (!hasMore) return null;

  return (
    <div className={cx('flex justify-center', className)}>
      <Button variant="secondary" size="sm" onClick={onLoadMore} disabled={isLoading}>
        {isLoading ? 'Loading…' : label}
      </Button>
    </div>
  );
}
